import { array, boolean, mixed, number, object, string } from 'yup'

import { validateApiResponse } from 'utils/validateApiResponse'

import { AirlockFormType } from './types'
import type { ConfigurationPublicViewV2, ConfigurationPublicViewV3, GetWorkspaceConfigurationResponse } from './types'

const workspaceConfigurationPermissionsSchema = object({
  admins: object({
    enableImportFromComputer: boolean().optional(),
    enableSelfReviewRestriction: boolean().optional(),
    enableViewFile: boolean().optional()
  })
    .optional()
    .unknown(true),
  members: object({
    enableImportFromComputer: boolean().optional(),
    enableViewFile: boolean().optional()
  })
    .optional()
    .unknown(true)
})
  .required()
  .unknown(true)

const workspaceConfigurationFormSchema = object({
  id: string().required(),
  type: mixed<AirlockFormType>().oneOf(Object.values(AirlockFormType)).required(),
  name: string().required()
})
  .required()
  .unknown(true)

const workspaceConfigurationPolicyUserSchema = object({
  id: string().required(),
  name: string().optional(),
  email: string().required()
})
  .nullable()
  .unknown(true)

const configurationPublicViewV2Schema = object({
  enabled: boolean().required(),
  status: mixed<'Enabled' | 'Disabled'>().oneOf(['Enabled', 'Disabled']).required(),
  bucketName: string().optional(),
  storageAccountName: string().optional(),
  containerName: string().optional(),
  downloadLinkExpiresInSeconds: number().required(),
  enableEmailDownloadLink: boolean().required(),
  permissions: workspaceConfigurationPermissionsSchema,
  verifiedResults: object({ autoApprove: boolean().optional() }).optional().unknown(true),
  unverifiedResults: object().optional().unknown(true)
})
  .unknown(true)
  .required()

const configurationPublicViewV3Schema = object({
  _id: string().required(),
  name: string().required(),
  enabled: boolean().required(),
  provider: string().required(),
  downloadLinkExpiresInSeconds: number().required(),
  enableEmailDownloadLink: boolean().required(),
  permissions: workspaceConfigurationPermissionsSchema,
  verifiedResults: object({ autoApprove: boolean().optional() }).optional().unknown(true),
  unverifiedResults: object().optional().unknown(true),
  importSettings: object().optional().unknown(true),
  managers: array(string().required()).optional(),
  forms: array(workspaceConfigurationFormSchema).optional(),
  createdBy: workspaceConfigurationPolicyUserSchema.optional(),
  modifiedBy: workspaceConfigurationPolicyUserSchema.optional(),
  createdAt: string().optional(),
  enabledAt: string().nullable().optional(),
  disabledAt: string().nullable().optional(),
  approvedBucketName: string().optional(),
  approvedStorageAccountName: string().optional(),
  approvedContainerName: string().optional(),
  airlockAccess: object({
    enabled: boolean().optional(),
    autoSharingOnApprovalEnabled: boolean().optional(),
    additionalInformationFormId: string().optional(),
    additionalInformationAvailable: boolean().optional()
  })
    .optional()
    .unknown(true)
})
  .unknown(true)
  .required()

const isEmptyWorkspaceConfiguration = (data: unknown): data is Record<string, never> =>
  typeof data === 'object' && data !== null && Object.keys(data).length === 0

const isNamedPolicyWorkspaceConfigurationPayload = (data: unknown): boolean =>
  typeof data === 'object' && data !== null && '_id' in data && 'provider' in data

export const parseGetWorkspaceConfigurationResponse = (data: unknown): GetWorkspaceConfigurationResponse => {
  if (data === null || data === undefined || isEmptyWorkspaceConfiguration(data)) {
    return {}
  }

  if (isNamedPolicyWorkspaceConfigurationPayload(data)) {
    return validateApiResponse<ConfigurationPublicViewV3>(configurationPublicViewV3Schema, data)
  }

  return validateApiResponse<ConfigurationPublicViewV2>(configurationPublicViewV2Schema, data)
}
